import EmailIcon from '@mui/icons-material/Email'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
    Box,
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import QRCode from 'qrcode'
import { useEffect, useRef } from 'react'
import { useBranch } from '../../context/branch_context'
import { ContactTypeEnum, useConfigs } from '../../context/configs_context'
import {
    parseTransferStockDestinationData,
    TransferStockInfo,
} from '../../pages/Products/TransferStock'
import {
    VehicleIdentificationType,
    VehicleModalityLabel,
    VehicleTypeLabel,
} from '../../pages/Vehicle/Home'
import { getDateInDDMMYYYYFormat, maskCurrency } from '../../utils/maks'

const StyledTableCell = styled(TableCell)(({}) => ({
    color: 'text.primary',
    border: 'none',
    padding: '1px 2px',
    fontSize: '11px',
}))

interface TransferStockNoteProps {
    transferStock: TransferStockInfo
}

const ContactIcon: Record<string, JSX.Element> = {
    Facebook: <FacebookIcon />,
    Instagram: <InstagramIcon />,
    Whatsapp: <WhatsAppIcon />,
    Endereço: <LocationOnIcon />,
    Telefone: <LocalPhoneIcon />,
    Email: <EmailIcon />,
}

function TransferStockNote({ transferStock }: TransferStockNoteProps) {
    const { configs, contactTypes, base64Logo } = useConfigs()
    const qrCodeRef = useRef<HTMLCanvasElement | null>(null)
    const { branches } = useBranch()

    useEffect(() => {
        if (transferStock.qrCode)
            QRCode.toCanvas(qrCodeRef.current, transferStock.qrCode, {
                width: 200,
            })
    }, [])

    const contacts = transferStock.originBranch
        ? transferStock.originBranch.contacts
        : configs.contacts
    const companyName =
        transferStock.originBranch && configs.showBranchNameInNote
            ? transferStock.originBranch.name
            : configs.companyName
    const companyLogo = transferStock.originBranch?.companyLogo
        ? branches.find((b) => b.id === transferStock.originBranch?.id)
              ?.companyLogo?.content
        : base64Logo
    const companySlogan = transferStock.originBranch?.companySlogan
        ? transferStock.originBranch.companySlogan
        : configs.companySlogan
    const companyDocument = transferStock.originBranch?.companyDocument
        ? transferStock.originBranch.companyDocument
        : configs.companyDocument

    const destinationData = parseTransferStockDestinationData(transferStock)

    return (
        <Stack
            direction="column"
            width="100%"
            px="20px"
            py="10px"
            height={'100vh'}
        >
            <Stack direction={'row'} width={'100%'}>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={transferStock.note ? '70%' : '100%'}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box
                            component={'img'}
                            src={companyLogo}
                            width={'220px'}
                            height={'110px'}
                            zIndex={'-1'}
                        />
                        <Stack textAlign={'center'} flex={'1'} px={'15px'}>
                            <Typography
                                fontSize={'14px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {companyName}
                            </Typography>
                            <Typography
                                fontSize={'9px'}
                                sx={{ wordBreak: 'break-word' }}
                                fontStyle={'italic'}
                                whiteSpace={'pre-line'}
                            >
                                {`${companySlogan}`}
                            </Typography>
                            <Typography
                                fontSize={'8px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {contactTypes
                                    .filter((ct) =>
                                        contacts.find(
                                            (c) => c.contactType.id === ct.id
                                        )
                                    )
                                    .map(
                                        (ct) =>
                                            `${ct.name}: ${contacts
                                                .filter(
                                                    (c) =>
                                                        c.contactType.id ===
                                                        ct.id
                                                )
                                                .map((c) => c.value)
                                                .join(', ')}`
                                    )
                                    .join('; ')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                {transferStock.note && (
                    <Stack
                        direction={'column'}
                        border={'1px solid black'}
                        borderRadius={'12px'}
                        width={'30%'}
                    >
                        <Stack direction={'row'} p={'10px'}>
                            <Box flex={'1'}>
                                <Typography fontSize={'11px'}>
                                    TIMBRADO N {transferStock.note?.timbrado}
                                </Typography>
                                <Typography fontSize={'11px'} mt={'1px'}>
                                    RUC: {companyDocument}
                                </Typography>
                                <Typography fontSize={'11px'} mt={'1px'}>
                                    Fecha início vigência:{' '}
                                    {getDateInDDMMYYYYFormat(
                                        transferStock.note?.iniD as any
                                    )}
                                </Typography>
                                <Typography fontSize={'11px'}>
                                    Fecha fin vigência:{' '}
                                    {transferStock.note?.endD
                                        ? getDateInDDMMYYYYFormat(
                                              transferStock.note.endD
                                          )
                                        : '---'}
                                </Typography>
                                <Typography
                                    fontSize={'14px'}
                                    mt={'10px'}
                                    fontWeight={'bold'}
                                    textAlign={'center'}
                                >
                                    {transferStock.bill}
                                </Typography>
                                <Typography
                                    fontSize={'14px'}
                                    mt={'5px'}
                                    fontWeight={'bold'}
                                    textAlign={'center'}
                                >
                                    Nota de remisión
                                </Typography>
                            </Box>
                        </Stack>
                    </Stack>
                )}
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                direction={'column'}
                padding={'5px'}
            >
                <Typography
                    fontWeight={'600'}
                    textAlign={'center'}
                    fontSize={'16px'}
                >
                    Destinatario de la mercaderia
                </Typography>
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Nombre o razón:{' '}
                        </Box>{' '}
                        {destinationData?.name}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            RUC/CI No:{' '}
                        </Box>{' '}
                        {destinationData?.document}
                    </Typography>
                </Stack>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                direction={'column'}
                padding={'5px'}
            >
                <Typography
                    fontWeight={'600'}
                    textAlign={'center'}
                    fontSize={'16px'}
                >
                    Datos del traslado
                </Typography>
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Motivo de emisión:{' '}
                        </Box>{' '}
                        {'Traslado por ventas'}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Anotación:{' '}
                        </Box>{' '}
                        {transferStock.annotation ?? '---'}
                    </Typography>
                </Stack>
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Responsable de la emisión:{' '}
                        </Box>{' '}
                        {'Emisor de la factura'}
                    </Typography>
                </Stack>
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Fecha de inicio del traslado:{' '}
                        </Box>{' '}
                        {transferStock.exitDate
                            ? getDateInDDMMYYYYFormat(transferStock.exitDate)
                            : '---'}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Fecha de fin del traslado:{' '}
                        </Box>{' '}
                        {transferStock.arrivalDate
                            ? getDateInDDMMYYYYFormat(transferStock.arrivalDate)
                            : '---'}
                    </Typography>
                </Stack>
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Ciudad punto de partida:{' '}
                        </Box>{' '}
                        {
                            transferStock.originBranch
                                ?.companyEmissionCityDescription
                        }
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Departamento punto de partida:{' '}
                        </Box>{' '}
                        {
                            transferStock.originBranch
                                ?.companyEmissionDepartmentDescription
                        }
                    </Typography>
                </Stack>
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Dirección punto de partida:{' '}
                        </Box>{' '}
                        {
                            transferStock.originBranch?.contacts.find(
                                (c) =>
                                    c.contactType.id === ContactTypeEnum.Address
                            )?.value
                        }
                    </Typography>
                </Stack>
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Ciudad punto de entrega:{' '}
                        </Box>{' '}
                        {destinationData?.companyEmissionCityDescription}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Departamento punto de entrega:{' '}
                        </Box>{' '}
                        {destinationData?.companyEmissionDepartmentDescription}
                    </Typography>
                </Stack>
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Dirección punto de entrega:{' '}
                        </Box>{' '}
                        {destinationData?.address}
                    </Typography>
                </Stack>
                <Stack
                    sx={{ width: '100%' }}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Kilometros de recorrido:{' '}
                        </Box>{' '}
                        {transferStock.estimatedDistance}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            No. de casa de salída:{' '}
                        </Box>{' '}
                        {transferStock.originBranch?.companyEmissionHouseNumber}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            No. de casa de entrega:{' '}
                        </Box>{' '}
                        {transferStock.originBranch?.companyEmissionHouseNumber}
                    </Typography>
                </Stack>
            </Stack>
            <Stack direction={'row'} width={'100%'}>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={'50%'}
                    padding={'5px'}
                >
                    <Typography
                        fontWeight={'600'}
                        textAlign={'center'}
                        fontSize={'16px'}
                    >
                        Datos del vehículo de transporte
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Tipo de transporte:{' '}
                        </Box>{' '}
                        {transferStock.vehicle?.type
                            ? VehicleTypeLabel[transferStock.vehicle?.type]
                            : ''}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Tipo de transporte:{' '}
                        </Box>{' '}
                        {transferStock.vehicle?.modality
                            ? VehicleModalityLabel[
                                  transferStock.vehicle?.modality
                              ]
                            : ''}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Responsable costo de flete:{' '}
                        </Box>{' '}
                        {'1'}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Condición de negociación:{' '}
                        </Box>{' '}
                        {'CFR'}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Marca del vehículo:{' '}
                        </Box>{' '}
                        {transferStock.vehicle?.brand}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            No. de identificación:{' '}
                        </Box>{' '}
                        {transferStock.vehicle?.idType ===
                        VehicleIdentificationType.Id
                            ? transferStock.vehicle?.document
                            : ''}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            No. de matrícula:{' '}
                        </Box>{' '}
                        {transferStock.vehicle?.idType ===
                        VehicleIdentificationType.Registration
                            ? transferStock.vehicle?.document
                            : ''}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Tipo de vehículo:{' '}
                        </Box>{' '}
                        {transferStock.vehicle?.modality
                            ? VehicleModalityLabel[
                                  transferStock.vehicle?.modality
                              ]
                            : ''}
                    </Typography>
                </Stack>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={'50%'}
                    padding={'5px'}
                >
                    <Typography
                        fontWeight={'600'}
                        textAlign={'center'}
                        fontSize={'16px'}
                    >
                        Datos del conductor del vehículo
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Naturaleza transp:{' '}
                        </Box>{' '}
                        {'1'}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Nombre del transp:{' '}
                        </Box>{' '}
                        {transferStock.employee?.name}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            No de CI del chofer:{' '}
                        </Box>{' '}
                        {transferStock.employee?.driverDocument}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Nombre del chofer:{' '}
                        </Box>{' '}
                        {transferStock.employee?.driverName}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Dirección:{' '}
                        </Box>{' '}
                        {''}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Nombre del agente:{' '}
                        </Box>{' '}
                        {transferStock.employee?.name}
                    </Typography>
                    <Typography fontSize={'11px'}>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                            RUC/CI del agente:{' '}
                        </Box>{' '}
                        {transferStock.employee?.document}
                    </Typography>
                </Stack>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderTop={'0'}
                borderRadius={'12px'}
                direction={'column'}
                flex={'1'}
            >
                <Typography
                    fontWeight={'600'}
                    textAlign={'center'}
                    fontSize={'16px'}
                >
                    Datos de la mercadería
                </Typography>
                <Table>
                    <TableHead>
                        <TableRow>
                            {configs.showIdOnNote && (
                                <StyledTableCell
                                    sx={{
                                        borderTop: '1px solid black',
                                        borderBottom: '1px solid black',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    CÓDIGO
                                </StyledTableCell>
                            )}
                            <StyledTableCell
                                sx={{
                                    borderTop: '1px solid black',
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                CÓDIGO BAR.
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderTop: '1px solid black',
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                UNIDAD DE MEDIDA
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderTop: '1px solid black',
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                DESC.
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderTop: '1px solid black',
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                CANT.
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderTop: '1px solid black',
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                PREC. VENTA
                            </StyledTableCell>
                            <StyledTableCell
                                sx={{
                                    borderTop: '1px solid black',
                                    borderBottom: '1px solid black',
                                    fontWeight: 'bold',
                                }}
                            >
                                LOCALIZACIÓN
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transferStock.products.map((product) => (
                            <TableRow sx={{ height: '0' }}>
                                {configs.showIdOnNote && (
                                    <StyledTableCell>
                                        {product.product.internalCodes[0]}
                                    </StyledTableCell>
                                )}
                                <StyledTableCell>
                                    {product.product.barcodes[0]}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {product.product.unitType}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {product.product.name}
                                </StyledTableCell>
                                <StyledTableCell>{product.qnt}</StyledTableCell>
                                <StyledTableCell>
                                    {maskCurrency(
                                        product.product.salePrice,
                                        product.product.coin
                                    )}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {product.product.localizations.find(
                                        (l) =>
                                            l.branchId ===
                                            transferStock.originBranch?.id
                                    )?.value ?? '---'}
                                </StyledTableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Stack>
            {transferStock.qrCode && (
                <Stack
                    width={'100%'}
                    border={'1px solid black'}
                    borderTop={'0'}
                    borderRadius={'12px'}
                    p="10px"
                >
                    <Stack direction={'row'} spacing={'15px'}>
                        <Box component="canvas" ref={qrCodeRef} />
                        <Stack direction="column" py="10px" spacing={'15px'}>
                            <Typography fontSize="12px">
                                Consulte la validez de esta factura eletrónica
                                con el documento CDC impreso en:
                            </Typography>
                            <Typography fontSize="13px">
                                https://ekuatia.set.gov.py/consultas/
                            </Typography>
                            <Typography fontSize="13px">
                                CDC: {transferStock.cdc}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Typography fontSize="12px" mt="5px">
                        ESTE DOCUMENTO ES UNA REPRESENTACIÓN GRÁFICA DE UN
                        DOCUMENTO ELETRÓNICO (XML)
                    </Typography>
                    <Typography my="10px" fontWeight="bold" fontSize="12px">
                        Información de interés del facturador eletrónico emisor
                    </Typography>
                    <Typography fontSize="11px">
                        Si su documento eletrónico presenta algún error, podrá
                        solicitar la modifcación dentro de las 72 horas
                        siguientes de la emisión de éste comprobante
                    </Typography>
                </Stack>
            )}
        </Stack>
    )
}

interface Props {
    transferStocks: TransferStockInfo[]
    handleClose(): void
    dontPrint?: boolean
}

export function TransferStockNoteDialog({
    handleClose,
    transferStocks,
    dontPrint,
}: Props) {
    const printRef = useRef<HTMLElement>()

    useEffect(() => {
        if (!dontPrint) {
            printRef.current!.style.visibility = 'hidden'
            printRef.current!.style.position = 'fixed'

            setTimeout(() => {
                printRef.current!.style.position = 'absolute'
                printRef.current!.className = 'printable'
                window.scroll(0, 0)
                window.print()
                handleClose()
            }, 100)
        }
    }, [])

    return (
        <Box id="note" ref={printRef}>
            <Stack direction={'column'}>
                {transferStocks.map((transferStock) => (
                    <>
                        <TransferStockNote transferStock={transferStock} />
                    </>
                ))}
            </Stack>
        </Box>
    )
}
